import React, { useState, useEffect } from 'react';
// Updated: 2025-01-05 09:15:00
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle, AlertCircle, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { useWebsites } from '@/context/WebsitesContext';
import { supabase } from '@/integrations/supabase/client';
import config from '@/config';

interface AgentStatus {
  isRunning: boolean;
  currentTask: string;
  progress: number;
  lastRun: string | null;
  error: string | null;
}

interface OnboardingAgentStatus {
  agent_status?: 'running' | 'completed' | 'failed' | 'idle' | null;
  agent_current_task?: string | null;
  agent_progress?: number | null;
  agent_started_at?: string | null;
  agent_completed_at?: string | null;
  agent_error?: string | null;
}

interface Concept {
  id: string;
  website_id: string;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
}

interface Keyword {
  id: string;
  website_id: string;
  keyword: string;
  concept_id: string;
  intent: 'transactional' | 'informational' | 'navigational' | 'mixed';
  search_volume?: number;
  created_at: string;
  updated_at: string;
}

interface Page {
  id: string;
  website_id: string;
  title: string;
  url: string;
  content?: string;
  content_type?: string;
  is_cornerstone?: boolean;
  concept_id?: string;
  needs_review?: boolean;
  created_at: string;
  updated_at: string;
}

interface ConceptWithDetails extends Concept {
  keywords: Keyword[];
  pages: Page[];
}

export const IntelligenceAgentCard: React.FC = () => {
  const { currentWebsite } = useWebsites();
  const [agentStatus, setAgentStatus] = useState<AgentStatus>({
    isRunning: false,
    currentTask: '',
    progress: 0,
    lastRun: null,
    error: null
  });
  const [conceptsWithDetails, setConceptsWithDetails] = useState<ConceptWithDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedConcepts, setExpandedConcepts] = useState<Set<string>>(new Set());
  const [onboardingStatus, setOnboardingStatus] = useState<OnboardingAgentStatus>({});
  const [conceptsCount, setConceptsCount] = useState<number>(0);

  useEffect(() => {
    if (currentWebsite?.id) {
      loadStatus();
      loadConcepts();
      // Automatically start agent if no concepts and not already running
      checkAndStartAgent();
    }
  }, [currentWebsite?.id]);

  // Poll status periodically while running (following onboarding patterns: light polling)
  useEffect(() => {
    if (!currentWebsite?.id) return;
    if (onboardingStatus.agent_status !== 'running') return;
    const t = setInterval(() => {
      loadStatus();
      loadConceptsCount();
    }, 4000);
    return () => clearInterval(t);
  }, [currentWebsite?.id, onboardingStatus.agent_status]);

  const loadStatus = async () => {
    if (!currentWebsite?.id) return;
    try {
      const { data, error } = await supabase
        .from('onboarding')
        .select('agent_status, agent_current_task, agent_progress, agent_started_at, agent_completed_at, agent_error')
        .eq('website_id', currentWebsite.id)
        .maybeSingle();
      if (!error && data) {
        setOnboardingStatus(data as OnboardingAgentStatus);
        // Reflect to local UI status for progress bar
        setAgentStatus(prev => ({
          ...prev,
          isRunning: data.agent_status === 'running',
          currentTask: data.agent_current_task || '',
          progress: typeof data.agent_progress === 'number' ? data.agent_progress : 0,
          lastRun: data.agent_completed_at || data.agent_started_at || prev.lastRun || null,
          error: data.agent_error || null
        }));
      }
    } catch (e) {
      // ignore
    }
  };

  const loadConceptsCount = async () => {
    if (!currentWebsite?.id) return;
    const { count } = await supabase
      .from('kw_concepts')
      .select('id', { count: 'exact', head: true })
      .eq('website_id', currentWebsite.id);
    setConceptsCount(count || 0);
  };

  const checkAndStartAgent = async () => {
    if (!currentWebsite?.id) return;

    try {
      // If onboarding status already running or completed, do nothing
      if (onboardingStatus.agent_status === 'running' || onboardingStatus.agent_status === 'completed') {
        return;
      }
      // If no concepts exist, start
      await loadConceptsCount();
      if (conceptsCount === 0) {
        await startAgent();
      }
    } catch (error) {
      console.error('Error checking concepts for auto-start:', error);
    }
  };

  const loadConcepts = async () => {
    if (!currentWebsite?.id) return;

    try {
      setLoading(true);
      
      // Fetch concepts with their keywords and pages
      const { data: concepts, error: conceptsError } = await supabase
        .from('kw_concepts')
        .select(`
          *,
          kw_keywords (
            id,
            keyword,
            intent,
            search_volume
          ),
          website_content (
            id,
            title,
            url,
            content_type,
            is_cornerstone,
            needs_review
          )
        `)
        .eq('website_id', currentWebsite.id)
        .order('created_at', { ascending: true });

      if (conceptsError) {
        throw new Error('Failed to fetch concepts');
      }

      // Transform the data to match our interface
      const transformedConcepts: ConceptWithDetails[] = (concepts || []).map(concept => ({
        ...concept,
        keywords: concept.kw_keywords || [],
        pages: concept.website_content || []
      }));

      setConceptsWithDetails(transformedConcepts);
    } catch (error) {
      console.error('Error loading concepts:', error);
    } finally {
      setLoading(false);
    }
  };

  const startAgent = async () => {
    if (!currentWebsite?.id) return;

    try {
      setLoading(true);
      setAgentStatus(prev => ({ ...prev, isRunning: true, currentTask: 'Starting...', progress: 0 }));

      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No access token found');
      }

      const response = await fetch(`${config.supabase.functionsUrl}/keyword-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          website_id: currentWebsite.id,
          action: 'start'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to start intelligence agent');
      }

      const result = await response.json();
      if (result.success) {
        setAgentStatus({
          isRunning: false,
          currentTask: 'Completed successfully',
          progress: 100,
          lastRun: new Date().toISOString(),
          error: null
        });
        
        // Reload concepts
        await loadConcepts();
      } else {
        throw new Error(result.error || 'Failed to start agent');
      }
    } catch (error) {
      console.error('Error starting agent:', error);
      setAgentStatus(prev => ({
        ...prev,
        isRunning: false,
        currentTask: 'Failed',
        error: error instanceof Error ? error.message : 'Unknown error'
      }));
    } finally {
      setLoading(false);
    }
  };

  const toggleConceptExpansion = (conceptId: string) => {
    setExpandedConcepts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(conceptId)) {
        newSet.delete(conceptId);
      } else {
        newSet.add(conceptId);
      }
      return newSet;
    });
  };

  if (!currentWebsite) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Intelligence Agent</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Status</span>
            {agentStatus.isRunning ? (
              <Badge variant="secondary">Analyzing website...</Badge>
            ) : agentStatus.error ? (
              <Badge variant="destructive">Failed</Badge>
            ) : conceptsWithDetails.length > 0 ? (
              <Badge variant="default">Complete</Badge>
            ) : (
              <Badge variant="outline">Waiting to start</Badge>
            )}
          </div>
          
          {!agentStatus.isRunning && conceptsWithDetails.length === 0 && (
            <div className="flex justify-end">
              <Button size="sm" onClick={startAgent}>Start now</Button>
            </div>
          )}

          {agentStatus.isRunning && (
            <div className="space-y-2">
              <Progress value={agentStatus.progress} className="h-2" />
              <p className="text-xs text-muted-foreground">{agentStatus.currentTask}</p>
            </div>
          )}
          
          {agentStatus.error && (
            <Alert variant="destructive">
              <AlertDescription>{agentStatus.error}</AlertDescription>
            </Alert>
          )}
          
          {conceptsWithDetails.length > 0 && (
            <div className="text-sm text-muted-foreground">
              Generated {conceptsWithDetails.length} content theme{conceptsWithDetails.length !== 1 ? 's' : ''} for your website structure
            </div>
          )}
        </div>

        {/* Content Structure - Information Architecture */}
        {conceptsWithDetails.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">Content Structure</h4>
              <Badge variant="outline" className="text-xs">
                {conceptsWithDetails.length} theme{conceptsWithDetails.length !== 1 ? 's' : ''}
              </Badge>
            </div>
            
            <div className="space-y-3">
              {conceptsWithDetails.map((concept) => {
                const isExpanded = expandedConcepts.has(concept.id);
                const totalKeywords = concept.keywords.length;
                const totalPages = concept.pages.length;
                
                return (
                  <div key={concept.id} className="border rounded-lg overflow-hidden">
                    <div 
                      className="p-3 cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => toggleConceptExpansion(concept.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          )}
                          <div>
                            <h5 className="font-medium text-sm">{concept.name}</h5>
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                              {concept.description}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2 text-xs text-muted-foreground">
                          <span>{totalKeywords} keywords</span>
                          <span>•</span>
                          <span>{totalPages} pages</span>
                        </div>
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <div className="border-t bg-muted/20 p-3 space-y-3">
                        {/* Keywords Section */}
                        {concept.keywords.length > 0 && (
                          <div>
                            <h6 className="text-xs font-medium text-muted-foreground mb-2">
                              Keywords ({concept.keywords.length})
                            </h6>
                            <div className="flex flex-wrap gap-1">
                              {concept.keywords.map((keyword) => (
                                <Badge 
                                  key={keyword.id} 
                                  variant="secondary" 
                                  className="text-xs"
                                >
                                  {keyword.keyword}
                                  {keyword.search_volume && (
                                    <span className="ml-1 text-muted-foreground">
                                      ({keyword.search_volume})
                                    </span>
                                  )}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Pages Section */}
                        {concept.pages.length > 0 && (
                          <div>
                            <h6 className="text-xs font-medium text-muted-foreground mb-2">
                              Pages ({concept.pages.length})
                            </h6>
                            <div className="space-y-1">
                              {concept.pages.map((page) => (
                                <div 
                                  key={page.id} 
                                  className="flex items-center justify-between text-xs bg-background rounded p-2"
                                >
                                  <div className="flex items-center gap-2 flex-1 min-w-0">
                                    <ExternalLink className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                    <span className="truncate">{page.title}</span>
                                    {page.is_cornerstone && (
                                      <Badge variant="outline" className="text-xs">
                                        Cornerstone
                                      </Badge>
                                    )}
                                    {page.needs_review && (
                                      <Badge variant="destructive" className="text-xs">
                                        Review needed
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="text-muted-foreground text-xs ml-2">
                                    {page.content_type}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Empty state */}
                        {concept.keywords.length === 0 && concept.pages.length === 0 && (
                          <div className="text-center py-4 text-xs text-muted-foreground">
                            No keywords or pages assigned yet
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
